import React from 'react';
import { usePIM } from '../context/PIMContext';
import { Trash, AlertTriangle, Info } from 'lucide-react';
import { Tooltip } from './ui/Tooltip';

export const PalletResultsTable = () => {
    const { palletCalculationResults, selectedPalletArrangement, setSelectedPalletArrangement } = usePIM();

    if (!palletCalculationResults || palletCalculationResults.length === 0) {
        return (
            <div className="h-full flex items-center justify-center text-stone-500 italic">
                No calculation results available. Please enter dimensions and calculate.
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-stone-900/50 rounded-xl overflow-hidden border border-stone-700">
            <div className="overflow-y-auto flex-1">
                <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 z-10 bg-stone-950 text-stone-500 text-xs uppercase font-semibold">
                        <tr>
                            <th className="px-4 py-3 border-b border-stone-800">Anordnung</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-center">B</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-center">L</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-center">Pro Lage</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-center">Lagen</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-center">Höhe</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-center">Gesamt</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-center">Gewicht (inkl. 25kg Palette)</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-center">Theor. Ausnutzung</th>
                            <th className="px-4 py-3 border-b border-stone-800 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {palletCalculationResults.map((res, idx) => {
                            // Determine row color based on index/efficiency
                            // Determine row color based on index/efficiency
                            let rowClass = "border-b border-stone-800 transition-colors cursor-pointer ";

                            if (idx < 2) {
                                // Green Tier
                                if (selectedPalletArrangement === res) {
                                    rowClass += "bg-green-900/40 border-l-4 border-l-green-500 text-white ";
                                } else {
                                    rowClass += "bg-green-900/20 hover:bg-green-900/30 text-green-100 ";
                                }
                            } else if (idx < 4) {
                                // Yellow Tier
                                if (selectedPalletArrangement === res) {
                                    rowClass += "bg-yellow-900/40 border-l-4 border-l-yellow-500 text-white ";
                                } else {
                                    rowClass += "bg-yellow-900/20 hover:bg-yellow-900/30 text-yellow-100 ";
                                }
                            } else {
                                // Red Tier
                                if (selectedPalletArrangement === res) {
                                    rowClass += "bg-stone-800/80 border-l-4 border-l-stone-600 text-white ";
                                } else {
                                    rowClass += "bg-stone-900/40 hover:bg-stone-800 text-stone-400 ";
                                }
                            }

                            // Override if weight limit exceeded
                            if (res.exceedsWeightLimit) {
                                rowClass += " !text-red-400 ";
                            }

                            return (
                                <tr
                                    key={idx}
                                    onClick={() => setSelectedPalletArrangement(res)}
                                    className={rowClass}
                                >
                                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                                        {res.name}
                                        {!res.isLogisticallyRecommended && (
                                            <Tooltip content="Möglicherweise instabil oder transporttechnisch ungeeignet (z.B. gekippte Master-Cartons)">
                                                <Info size={14} className="text-yellow-500" />
                                            </Tooltip>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">{res.boxesInWidth}</td>
                                    <td className="px-4 py-3 text-center">{res.boxesInLength}</td>
                                    <td className="px-4 py-3 text-center font-bold">{res.boxesPerLayer}</td>
                                    <td className="px-4 py-3 text-center">{res.layers}</td>
                                    <td className="px-4 py-3 text-center">{res.totalHeight} mm</td>
                                    <td className="px-4 py-3 text-center font-bold">{res.totalBoxes}</td>
                                    <td className="px-4 py-3 text-center flex items-center justify-center gap-1">
                                        {res.exceedsWeightLimit && (
                                            <Tooltip content="Gewichtslimit der Palette überschritten!">
                                                <AlertTriangle size={14} className="text-red-500" />
                                            </Tooltip>
                                        )}
                                        {res.totalWeight.toFixed(2)} kg
                                    </td>
                                    <td className="px-4 py-3 text-center font-bold">{res.theoreticalVolumeUtilization?.toFixed(2) || 0} %</td>
                                    <td className="px-4 py-3 text-right">
                                        <button className="p-1 hover:bg-stone-800 rounded text-stone-400 hover:text-white">
                                            <Trash size={14} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
